import React, { useEffect, useRef } from "react";
import { canAnimate, clamp, ease, finePointer, lerp, seeded } from "./motion";
import { buildGyri, drawBrain, inBrain } from "./brain";

const WORKERS = 45;
const SPARE = 75;
const STEPS = ["Intelligence", "becomes a workforce", "You lead."];
const SECTIONS = [
  ["Sales", ["Deal closed", "Meeting booked", "Renewal signed"]],
  ["Support", ["Ticket resolved", "Customer onboarded", "Refund handled"]],
  ["Finance", ["Invoice sent", "Books closed", "Payroll run"]],
  ["Research", ["Report shipped", "Market mapped", "Sources verified"]],
  ["Operations", ["Queue cleared", "Order fulfilled", "Vendor paid"]],
];
const ROWS = [12, 15, 18];
const ARC = [Math.PI * 1.14, Math.PI * 1.86];

function brainNodes() {
  const random = seeded(21);
  const nodes = [];
  for (let tries = 0; tries < 6000 && nodes.length < WORKERS + SPARE; tries += 1) {
    const x = (random() * 2 - 1) * 1.25;
    const y = (random() * 2 - 1) * 0.95;
    if (!inBrain(x, y) || nodes.some((n) => Math.hypot(n.x - x, n.y - y) < 0.155)) continue;
    nodes.push({ x, y });
  }
  nodes.forEach((n, i) => {
    n.links = nodes
      .map((m, j) => [Math.hypot(m.x - n.x, m.y - n.y), j])
      .filter(([, j]) => j !== i)
      .sort((p, q) => p[0] - q[0])
      .slice(0, 3)
      .map(([, j]) => j);
  });
  // The workers come from all over the brain, not one corner of it.
  const shuffle = seeded(3);
  const order = nodes
    .map((_, i) => [shuffle(), i])
    .sort((p, q) => p[0] - q[0])
    .map(([, i]) => i);
  const workers = order.slice(0, WORKERS);
  return { nodes, workers, isWorker: new Set(workers) };
}

// One member of the workforce, facing you from behind its laptop. lit = playing its part.
function member(g, x, y, k, t, lit, seed) {
  g.lineWidth = Math.max(0.8, k * 0.08);
  g.strokeStyle = "#191919";
  g.beginPath();
  if (g.roundRect) g.roundRect(x - 0.56 * k, y - 1.62 * k, 1.12 * k, 1.1 * k, 0.32 * k);
  else g.rect(x - 0.56 * k, y - 1.62 * k, 1.12 * k, 1.1 * k);
  g.fillStyle = "#fff";
  g.fill();
  g.stroke();
  const hy = y - 2.08 * k;
  g.beginPath();
  g.ellipse(x, hy, 0.4 * k, 0.47 * k, 0, 0, Math.PI * 2);
  g.fillStyle = "#fff";
  g.fill();
  g.stroke();
  g.fillStyle = "#191919";
  g.beginPath();
  if (g.roundRect) g.roundRect(x - 0.32 * k, hy - 0.16 * k, 0.64 * k, 0.2 * k, 0.1 * k);
  else g.rect(x - 0.32 * k, hy - 0.16 * k, 0.64 * k, 0.2 * k);
  g.fill();
  const scan = ((t / (lit ? 380 : 1400) + seed) % 1 + 1) % 1;
  g.fillStyle = "rgba(255,255,255,0.9)";
  g.fillRect(x - 0.26 * k + 0.44 * k * scan, hy - 0.13 * k, 0.1 * k, 0.13 * k);
  // The laptop lid, then the desk in front
  g.fillStyle = lit ? "#191919" : "#3a3a3a";
  g.beginPath();
  if (g.roundRect) g.roundRect(x - 0.62 * k, y - 1.3 * k, 1.24 * k, 0.8 * k, 0.08 * k);
  else g.rect(x - 0.62 * k, y - 1.3 * k, 1.24 * k, 0.8 * k);
  g.fill();
  g.beginPath();
  g.arc(x, y - 0.9 * k, 0.09 * k, 0, Math.PI * 2);
  g.fillStyle = lit ? "#ffffff" : "rgba(255,255,255,0.3)";
  g.fill();
  g.fillStyle = "#fff";
  g.fillRect(x - 1.05 * k, y - 0.5 * k, 2.1 * k, 0.78 * k);
  g.strokeRect(x - 1.05 * k, y - 0.5 * k, 2.1 * k, 0.78 * k);
  if (lit) {
    // A spark of finished work, rising off the laptop
    const rise = ((t / 900 + seed) % 1 + 1) % 1;
    g.beginPath();
    g.arc(x + (seed - 0.5) * k, y - 1.4 * k - rise * 2.6 * k, Math.max(1.2, 0.12 * k), 0, Math.PI * 2);
    g.fillStyle = `rgba(25,25,25,${0.8 * (1 - rise)})`;
    g.fill();
  }
}

// You, seen from the hall: relaxed in a lounge chair at the front, tablet in hand.
function you(g, x, y, k, lit) {
  g.fillStyle = "#191919";
  g.beginPath();
  g.ellipse(x, y - 2.05 * k, 0.78 * k, 0.32 * k, 0, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.arc(x, y - 2.5 * k, 0.34 * k, 0, Math.PI * 2);
  g.fill();
  g.save();
  g.translate(x + 0.72 * k, y - 2.45 * k);
  g.rotate(0.25);
  g.fillStyle = lit ? "#ffffff" : "#e6e6e6";
  g.fillRect(-0.28 * k, -0.2 * k, 0.56 * k, 0.4 * k);
  g.strokeStyle = "#191919";
  g.lineWidth = 1.2;
  g.strokeRect(-0.28 * k, -0.2 * k, 0.56 * k, 0.4 * k);
  g.restore();
  g.beginPath();
  if (g.roundRect) g.roundRect(x - 1.05 * k, y - 2.05 * k, 2.1 * k, 1.9 * k, 0.45 * k);
  else g.rect(x - 1.05 * k, y - 2.05 * k, 2.1 * k, 1.9 * k);
  g.fillStyle = "#ffffff";
  g.fill();
  g.strokeStyle = "#191919";
  g.lineWidth = 1.2;
  g.stroke();
  for (const side of [-1, 1]) {
    g.beginPath();
    if (g.roundRect) g.roundRect(x + side * 1.05 * k - (side < 0 ? 0.36 * k : 0), y - 1.1 * k, 0.36 * k, 1.0 * k, 0.12 * k);
    else g.rect(x + side * 1.05 * k - (side < 0 ? 0.36 * k : 0), y - 1.1 * k, 0.36 * k, 1.0 * k);
    g.fillStyle = "#ececec";
    g.fill();
    g.stroke();
  }
}

const MISSIONS = [
  "Grow revenue 30% this quarter",
  "Launch in the UK",
  "Hire 3 founding engineers",
  "Activate 24 customer workspaces",
  "Close the books by Friday",
];
const DAY = 18000;
const nightness = (hr) =>
  hr >= 21 || hr < 5 ? 1 : hr >= 19 && hr < 21 ? (hr - 19) / 2 : hr >= 5 && hr < 7 ? 1 - (hr - 5) / 2 : 0;
const clockText = (hr) => {
  const hh = Math.floor(hr);
  const mm = Math.floor((hr - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

// Intelligence becomes a workforce. You lead: a brain of neurons breaks apart and each neuron takes
// its seat in the workforce. Then it runs around the clock. You set the mission, they carry it
// through the day and the night, and only the exceptions come to you.
export default function WorkforceFormation() {
  const track = useRef(null);
  const host = useRef(null);
  const canvasRef = useRef(null);
  const stepsRef = useRef(null);
  const hintRef = useRef(null);
  useEffect(() => {
    const rail = track.current;
    const box = host.current;
    const canvas = canvasRef.current;
    if (!rail || !box || !canvas || !window.matchMedia) return undefined;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return undefined;
    const animate = canAnimate();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { nodes, workers, isWorker } = brainNodes();
    const gyri = buildGyri();
    const fired = new Float64Array(nodes.length).fill(-1e9);
    let impulses = [];
    let lastSpark = 0;
    let lastPoke = 0;
    const pointer = { x: 0, y: 0, on: false };
    const fine = finePointer();
    const lateHint = fine ? "It runs around the clock. Click to set a new mission." : "Tap to set a new mission.";
    const seats = [];
    const jitter = seeded(40);
    ROWS.forEach((n, row) => {
      for (let k = 0; k < n; k += 1) {
        const th = ARC[0] + ((k + 0.5) / n) * (ARC[1] - ARC[0]);
        seats.push({ row, th, section: Math.min(SECTIONS.length - 1, Math.floor(((th - ARC[0]) / (ARC[1] - ARC[0])) * SECTIONS.length)), seed: jitter() });
      }
    });
    let w = 0;
    let h = 0;
    let shown = animate ? 0 : 1;
    let raf = 0;
    let visible = false;
    let step = -1;
    let downbeat = -1e9;
    let mission = 0;
    let clockStart = null;
    let done = animate ? 0 : 1204;
    let lastT = 0;
    let exception = null;
    let lastException = 0;
    let brief = null;
    let lastHour = 9;
    let nightStart = 0;
    const sky = seeded(90);
    const stars = Array.from({ length: 46 }, () => [sky(), sky(), sky()]);
    let chips = [];
    let lastChip = 0;
    const resize = () => {
      const rect = box.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const hall = () => {
      const phone = w < 700;
      const cx = w / 2;
      const cy = h * (phone ? 0.9 : 1.02);
      const rx = (phone ? [0.25, 0.34, 0.43] : [0.2, 0.29, 0.38]).map((f) => f * w);
      const ry = (phone ? [0.33, 0.47, 0.61] : [0.4, 0.58, 0.76]).map((f) => f * h);
      const size = (phone ? [8.5, 7.4, 6.4] : [13, 11.2, 9.6]).map((v) => v);
      return { cx, cy, rx, ry, size, phone };
    };
    const seatAt = (s, H) => [H.cx + Math.cos(s.th) * H.rx[s.row], H.cy + Math.sin(s.th) * H.ry[s.row]];
    const progress = () => {
      if (!animate) return 1;
      const railRect = rail.getBoundingClientRect();
      const pin = parseFloat(getComputedStyle(box).top) || 0;
      const travel = railRect.height - box.offsetHeight;
      return travel > 40 ? clamp((pin - railRect.top) / travel) : 1;
    };
    const pill = (x, y, text, strong) => {
      ctx.font = `600 ${strong ? 12 : 11}px Manrope, Arial, sans-serif`;
      const pw = ctx.measureText(text).width + (strong ? 22 : 28);
      const ph = strong ? 24 : 22;
      const px = clamp(x - pw / 2, 6, w - pw - 6);
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(px, y - ph, pw, ph, ph / 2);
      else ctx.rect(px, y - ph, pw, ph);
      ctx.fillStyle = strong ? "#191919" : "#ffffff";
      ctx.fill();
      ctx.strokeStyle = "rgba(25,25,25,0.16)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.textBaseline = "middle";
      if (!strong) {
        ctx.beginPath();
        ctx.arc(px + 12, y - ph / 2, 4.5, 0, Math.PI * 2);
        ctx.fillStyle = "#191919";
        ctx.fill();
      }
      ctx.fillStyle = strong ? "#ffffff" : "#191919";
      ctx.fillText(text, px + (strong ? 11 : 21), y - ph / 2 + 0.5);
    };
    const frame = (t) => {
      raf = visible ? requestAnimationFrame(frame) : 0;
      shown += (progress() - shown) * (animate ? 0.08 : 1);
      const P = shown;
      const brainAmt = animate ? 1 - clamp((P - 0.3) / 0.28) : 0;
      const leadAmt = animate ? ease(clamp((P - 0.5) / 0.22)) : 1;
      const live = animate && P > 0.6;
      ctx.clearRect(0, 0, w, h);
      const nextStep = P < 0.34 ? 0 : P < 0.74 ? 1 : 2;
      if (nextStep !== step && stepsRef.current) {
        step = nextStep;
        [...stepsRef.current.children].forEach((el, k) => el.classList.toggle("is-on", k <= step));
      }
      if (hintRef.current && animate) {
        const early = fine && P < 0.32;
        const text = early ? "Move over the brain to spark a thought." : lateHint;
        if (hintRef.current.textContent !== text) hintRef.current.textContent = text;
        hintRef.current.style.opacity = (early ? 1 : clamp((P - 0.8) / 0.15)).toFixed(2);
      }
      const r = w < 700 ? Math.min(h * 0.34, w * 0.36) : Math.min(h * 0.38, w * 0.21);
      const bx = w * 0.5;
      const by = h * 0.47;
      const breathe = 1 + Math.sin(t * 0.0018) * 0.012;
      const nodeAt = (n) => [bx + n.x * r * breathe, by + n.y * r * breathe];
      // The brain, then its neurons firing in cascades along curved synapses
      if (brainAmt > 0) {
        ctx.globalAlpha = clamp(brainAmt * 1.5);
        drawBrain(ctx, bx, by, r * breathe, t, gyri, brainAmt);
        ctx.globalAlpha = brainAmt;
        const curve = (a, b) => {
          const [x0, y0] = nodeAt(nodes[a]);
          const [x1, y1] = nodeAt(nodes[b]);
          const len = Math.hypot(x1 - x0, y1 - y0) || 1;
          const bend = 0.22 * len * ((a * 7 + b * 13) % 2 ? 1 : -1);
          return { x0, y0, x1, y1, cx: (x0 + x1) / 2 - ((y1 - y0) / len) * bend, cy: (y0 + y1) / 2 + ((x1 - x0) / len) * bend, len };
        };
        const at = (c, q) => [
          (1 - q) * (1 - q) * c.x0 + 2 * (1 - q) * q * c.cx + q * q * c.x1,
          (1 - q) * (1 - q) * c.y0 + 2 * (1 - q) * q * c.cy + q * q * c.y1,
        ];
        const spawn = (from, not) => {
          const links = nodes[from].links.filter((l) => l !== not);
          const to = links[Math.floor(Math.random() * links.length)];
          if (to === undefined) return;
          impulses.push({ from, to, t0: t, dur: 240 + curve(from, to).len * 3.2 });
        };
        if (animate) {
          if (impulses.length < 14 && t - lastSpark > 160) {
            lastSpark = t;
            spawn(Math.floor(Math.random() * nodes.length));
          }
          if (pointer.on && t - lastPoke > 130) {
            let best = -1;
            let bestD = 70;
            nodes.forEach((n, i) => {
              const [nx, ny] = nodeAt(n);
              const d = Math.hypot(nx - pointer.x, ny - pointer.y);
              if (d < bestD) (best = i), (bestD = d);
            });
            if (best >= 0) {
              lastPoke = t;
              fired[best] = t;
              for (let n = 0; n < 3; n += 1) spawn(best);
            }
          }
          const arrived = impulses.filter((m) => t - m.t0 >= m.dur);
          impulses = impulses.filter((m) => t - m.t0 < m.dur);
          for (const m of arrived) {
            fired[m.to] = t;
            if (impulses.length < 44 && Math.random() < 0.72) spawn(m.to, m.from);
            if (impulses.length < 44 && Math.random() < 0.28) spawn(m.to, m.from);
          }
        }
        const drift = (1 - brainAmt) * 20;
        const spare = new Path2D();
        nodes.forEach((n, i) => {
          if (isWorker.has(i)) return;
          const [x, y] = nodeAt(n);
          spare.moveTo(x + 1.8, y - drift);
          spare.arc(x, y - drift, 1.8, 0, Math.PI * 2);
        });
        ctx.fillStyle = "rgba(25,25,25,0.55)";
        ctx.fill(spare);
        for (let i = 0; i < nodes.length; i += 1) {
          const q = (t - fired[i]) / 650;
          if (q < 0 || q >= 1) continue;
          const [x, y] = nodeAt(nodes[i]);
          ctx.beginPath();
          ctx.arc(x, y, 3 + q * 11, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(25,25,25,${0.55 * (1 - q)})`;
          ctx.lineWidth = 1.2;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(x, y, 2.6 * (1 - q) + 1.4, 0, Math.PI * 2);
          ctx.fillStyle = "#191919";
          ctx.fill();
        }
        ctx.lineCap = "round";
        for (const m of impulses) {
          const c = curve(m.from, m.to);
          const q = (t - m.t0) / m.dur;
          ctx.beginPath();
          for (let k = 0; k <= 10; k += 1) {
            const [x, y] = at(c, Math.max(0, q - 0.4) + (Math.min(q, 0.4) * k) / 10);
            if (k) ctx.lineTo(x, y);
            else ctx.moveTo(x, y);
          }
          ctx.strokeStyle = "rgba(25,25,25,0.85)";
          ctx.lineWidth = 1.8;
          ctx.stroke();
          const [hx, hy] = at(c, q);
          ctx.beginPath();
          ctx.arc(hx, hy, 2.3, 0, Math.PI * 2);
          ctx.fillStyle = "#191919";
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
      const H = hall();
      const k = H.phone ? 13 : 20;
      const seat = [H.cx, h * (H.phone ? 0.86 : 0.97)];
      // Around the clock: a day passes every 18 seconds and the work never stops.
      if (live && clockStart === null) clockStart = t;
      if (!live) clockStart = null;
      const hour = clockStart === null ? 9 : (9 + ((t - clockStart) / DAY) * 24) % 24;
      const night = live ? nightness(hour) : 0;
      const dt = Math.min(60, t - (lastT || t));
      lastT = t;
      if (live) done += dt * (0.045 + night * 0.01);
      if (live && lastHour < 21 && hour >= 21) nightStart = done;
      if (live && lastHour < 8 && hour >= 8 && lastHour > 4) brief = { t, tasks: Math.round(done - nightStart) };
      lastHour = hour;
      if (night > 0) {
        ctx.save();
        ctx.translate(w / 2, h * 0.6);
        ctx.scale(1, (h * 0.6) / (w * 0.48));
        const dusk = ctx.createRadialGradient(0, 0, 0, 0, 0, w * 0.48);
        dusk.addColorStop(0, `rgba(25,25,25,${0.075 * night})`);
        dusk.addColorStop(1, "rgba(25,25,25,0)");
        ctx.fillStyle = dusk;
        ctx.fillRect(-w / 2, -w, w, w * 2);
        ctx.restore();
        for (const [sx, sy, tw] of stars) {
          ctx.globalAlpha = night * (0.35 + 0.65 * Math.abs(Math.sin(t * 0.0012 + tw * 9)));
          ctx.fillStyle = "#191919";
          ctx.fillRect(sx * w, 40 + sy * h * 0.32, 1.6, 1.6);
        }
        ctx.globalAlpha = 1;
      }
      // Sections pick up work on their own, in turn, all day and all night
      const turn = Math.floor(t / 1500);
      const active = new Set([turn % SECTIONS.length, (turn + 2) % SECTIONS.length]);
      const sinceBeat = t - downbeat;
      if (leadAmt > 0) {
        ctx.font = `600 ${H.phone ? 9 : 11}px Manrope, Arial, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        SECTIONS.forEach(([full], n) => {
          const name = w < 400 && full === "Operations" ? "Ops" : full;
          const th = ARC[0] + ((n + 0.5) / SECTIONS.length) * (ARC[1] - ARC[0]);
          const half = ctx.measureText(name.toUpperCase()).width / 2 + 6;
          const x = clamp(H.cx + Math.cos(th) * H.rx[2] * (H.phone ? 1.02 : 1.13), half, w - half);
          const lift = H.phone && (n === 0 || n === SECTIONS.length - 1) ? (w < 400 ? 28 : 16) : 0;
          const y = H.cy + Math.sin(th) * H.ry[2] * (H.phone ? 1.04 : 1.1) - 3.1 * H.size[2] - 8 - lift;
          ctx.globalAlpha = leadAmt;
          ctx.fillStyle = live && active.has(n) ? "#191919" : "rgba(25,25,25,0.35)";
          ctx.fillText(name.toUpperCase(), x, y);
        });
        ctx.globalAlpha = 1;
        ctx.textAlign = "left";
      }
      // Neurons take their seats, back rows first so the front rows sit in front
      const order = seats.map((_, i) => i).sort((p, q) => seats[q].row - seats[p].row);
      const places = [];
      for (const i of order) {
        const st = seats[i];
        const q = animate ? ease(clamp((P - 0.3 - i * 0.0055) / 0.3)) : 1;
        const [sx, sy] = seatAt(st, H);
        const [nx, ny] = nodeAt(nodes[workers[i]]);
        const x = lerp(nx, sx, q);
        const y = lerp(ny, sy, q);
        const wave = sinceBeat < 1600 ? clamp(1 - Math.abs(sinceBeat / 900 - Math.abs(st.th - Math.PI * 1.5) * 0.9)) : 0;
        const lit = live && (active.has(st.section) || wave > 0.2 || (night > 0.5 && Math.sin(t * 0.002 + st.seed * 40) > 0.6));
        if (q < 0.5) {
          ctx.beginPath();
          ctx.arc(x, y, 2 + q * 4.6, 0, Math.PI * 2);
          ctx.fillStyle = "#191919";
          ctx.fill();
        } else {
          ctx.globalAlpha = clamp((q - 0.5) / 0.3);
          member(ctx, x, y - wave * 6, H.size[st.row], t, lit, st.seed);
          ctx.globalAlpha = 1;
        }
        places.push({ i, s: st, x, y, q, lit });
      }
      // Now and then, an exception comes to you. One tap, and the work carries on.
      if (live && !exception && t - lastException > 5200) {
        const ready = places.filter((p) => p.q >= 1 && p.x > 80 && p.x < w - 80);
        const pick = ready[Math.floor(Math.random() * ready.length)];
        if (pick) exception = { pick, t };
      }
      if (leadAmt > 0) {
        ctx.globalAlpha = leadAmt;
        const pending = exception && t - exception.t < 1100;
        you(ctx, seat[0], seat[1] + (1 - leadAmt) * 20, k, pending || (brief && t - brief.t < 3200));
        ctx.globalAlpha = 1;
        if (leadAmt > 0.6) {
          ctx.globalAlpha = (leadAmt - 0.6) / 0.4;
          pill(seat[0] - 1.6 * k - 30, seat[1] - 0.9 * k, "You", true);
          pill(seat[0], seat[1] - 3.1 * k, `Mission · ${MISSIONS[mission]}`, true);
          ctx.globalAlpha = 1;
        }
      }
      if (exception) {
        const age = t - exception.t;
        const { pick } = exception;
        const top = [pick.x, pick.y - 2.8 * H.size[pick.s.row]];
        const hand = [seat[0] + 0.7 * k, seat[1] - 2.5 * k];
        ctx.setLineDash([2, 4]);
        ctx.beginPath();
        ctx.moveTo(top[0], top[1] + 6);
        const reach = clamp(age / 500);
        ctx.lineTo(top[0] + (hand[0] - top[0]) * reach, top[1] + 6 + (hand[1] - top[1] - 6) * reach);
        ctx.strokeStyle = `rgba(25,25,25,${0.45 * clamp((2000 - age) / 400)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = clamp((2000 - age) / 400);
        pill(top[0], top[1], age < 1100 ? "Needs you" : "Approved ✓", age < 1100);
        ctx.globalAlpha = 1;
        if (age > 2000) {
          exception = null;
          lastException = t;
        }
      }
      if (brief) {
        const age = t - brief.t;
        if (age > 3200) brief = null;
        else {
          ctx.globalAlpha = clamp(age / 250) * clamp((3200 - age) / 400);
          const tasks = brief.tasks.toLocaleString("en-US");
          const text = H.phone ? `While you slept: ${tasks} tasks done` : `Morning brief · While you slept: ${tasks} tasks done, 1 needed you`;
          pill(seat[0], seat[1] - 3.1 * k - 30, text, false);
          ctx.globalAlpha = 1;
        }
      }
      // The clock and the tally, bottom left and right
      if (leadAmt > 0.6) {
        ctx.globalAlpha = (leadAmt - 0.6) / 0.4;
        const cx0 = 20;
        const cy0 = h - (H.phone ? 26 : 30);
        const big = H.phone ? 18 : 22;
        ctx.beginPath();
        ctx.arc(cx0, cy0, 14, 0, Math.PI * 2);
        ctx.fillStyle = night > 0.5 ? "#191919" : "#ffffff";
        ctx.fill();
        ctx.strokeStyle = "#191919";
        ctx.lineWidth = 1.2;
        ctx.stroke();
        const ha = ((hour % 12) / 12) * Math.PI * 2 - Math.PI / 2;
        const ma = (hour % 1) * Math.PI * 2 - Math.PI / 2;
        ctx.strokeStyle = night > 0.5 ? "#ffffff" : "#191919";
        ctx.beginPath();
        ctx.moveTo(cx0, cy0);
        ctx.lineTo(cx0 + Math.cos(ha) * 7, cy0 + Math.sin(ha) * 7);
        ctx.moveTo(cx0, cy0);
        ctx.lineTo(cx0 + Math.cos(ma) * 11, cy0 + Math.sin(ma) * 11);
        ctx.stroke();
        ctx.fillStyle = "#191919";
        ctx.textBaseline = "middle";
        ctx.font = `500 ${big}px Manrope, Arial, sans-serif`;
        ctx.fillText(clockText(hour), cx0 + 26, cy0 - 7);
        ctx.font = "600 11px Manrope, Arial, sans-serif";
        ctx.fillStyle = "rgba(25,25,25,0.6)";
        ctx.fillText(night > 0.5 ? "Night · still working" : "Day · working", cx0 + 26, cy0 + 13);
        ctx.textAlign = "right";
        ctx.font = "600 11px Manrope, Arial, sans-serif";
        ctx.fillText("Tasks completed", w - 2, cy0 - 18);
        ctx.fillStyle = "#191919";
        ctx.font = `500 ${big}px Manrope, Arial, sans-serif`;
        ctx.fillText(Math.floor(done).toLocaleString("en-US"), w - 2, cy0 + 4);
        ctx.textAlign = "left";
        ctx.globalAlpha = 1;
      }
      // Results from the sections at work
      if (live && t - lastChip > 700 && chips.length < 3) {
        lastChip = t;
        const playing = places.filter((p) => p.lit && p.q >= 1 && p.x > 70 && p.x < w - 70 && (!exception || p !== exception.pick));
        const pick = playing[Math.floor(Math.random() * playing.length)];
        const top = pick && pick.y - 2.7 * H.size[pick.s.row];
        // Keep the cards clear of each other
        if (pick && !chips.some((c) => Math.abs(c.x - pick.x) < 150 && Math.abs(c.y - top) < 28)) {
          const tasks = SECTIONS[pick.s.section][1];
          chips.push({ x: pick.x, y: pick.y - 2.7 * H.size[pick.s.row], text: tasks[Math.floor(Math.random() * tasks.length)], t });
        }
      }
      chips = chips.filter((c) => t - c.t < 2000);
      for (const c of chips) {
        const age = t - c.t;
        if (age < 0) continue;
        ctx.globalAlpha = clamp(age / 200) * clamp((2000 - age) / 350);
        pill(c.x, c.y - (age / 2000) * 10, c.text, false);
        ctx.globalAlpha = 1;
      }
    };
    const onMove = (e) => {
      if (e.pointerType !== "mouse") return;
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.on = true;
    };
    const onLeave = () => {
      pointer.on = false;
    };
    const onClick = () => {
      if (!animate || shown < 0.75) return;
      downbeat = performance.now();
      mission = (mission + 1) % MISSIONS.length;
      const H = hall();
      chips = [0, 9, 20, 33, 42].map((i, n) => {
        const st = seats[i];
        const [x, y] = seatAt(st, H);
        const tasks = SECTIONS[st.section][1];
        return { x, y: y - 2.7 * H.size[st.row], text: tasks[n % tasks.length], t: downbeat + 300 + n * 90 };
      });
    };
    resize();
    visible = true;
    frame(performance.now());
    visible = false;
    const observer = window.IntersectionObserver
      ? new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          if (visible && !raf) raf = requestAnimationFrame(frame);
        })
      : null;
    observer?.observe(box);
    const resizer = window.ResizeObserver
      ? new ResizeObserver(() => {
          if (Math.abs(box.getBoundingClientRect().width - w) > 2) resize();
        })
      : null;
    resizer?.observe(box);
    if (animate) {
      box.addEventListener("click", onClick);
      box.addEventListener("pointermove", onMove);
      box.addEventListener("pointerleave", onLeave);
    }
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      resizer?.disconnect();
      box.removeEventListener("click", onClick);
      box.removeEventListener("pointermove", onMove);
      box.removeEventListener("pointerleave", onLeave);
    };
  }, []);
  return (
    <div ref={track} className="bm-formation-track">
      <div ref={host} className="bm-formation" aria-hidden="true">
        <canvas ref={canvasRef} />
        <div ref={stepsRef} className="bm-formation-steps">
          {STEPS.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
        <span ref={hintRef} className="bm-formation-hint" />
      </div>
    </div>
  );
}
